import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Play,
  Filter,
  PhoneCall,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useClinic } from "@/contexts/ClinicContext";
import {
  FollowUp,
  fetchFollowUps,
  updateFollowUpStatus,
  updateFollowUpNotes,
} from "@/services/follow-up-service";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TYPE_LABELS: Record<string, string> = {
  RECALL_CALLBACK: "Recall 6M",
  NOSHOW_CALLBACK: "No-Show",
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendente",
  IN_PROGRESS: "Em andamento",
  DONE: "Concluido",
  CANCELLED: "Cancelado",
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-amber-100 text-amber-800",
  IN_PROGRESS: "bg-blue-100 text-blue-800",
  DONE: "bg-green-100 text-green-800",
  CANCELLED: "bg-gray-100 text-gray-500",
};

function formatPhone(phone: string | null): string {
  if (!phone) return "-";
  const clean = phone.replace(/\D/g, "").replace(/^55/, "");
  if (clean.length === 11)
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  if (clean.length === 10)
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
  return phone;
}

function formatDateBR(dateStr: string | null): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function FollowUps() {
  const { selectedClinicId } = useClinic();
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const { toast } = useToast();

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>("PENDING");
  const [filterType, setFilterType] = useState<string>("ALL");
  const [filterName, setFilterName] = useState("");

  // Notes dialog
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(null);
  const [notesText, setNotesText] = useState("");

  const PAGE_SIZE = 20;

  const loadFollowUps = async () => {
    if (!selectedClinicId) {
      toast({
        title: "Selecione uma clinica no topo da pagina",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const data = await fetchFollowUps(
        selectedClinicId,
        filterStatus === "ALL" ? undefined : filterStatus,
        filterType === "ALL" ? undefined : filterType,
        currentPage,
        PAGE_SIZE,
      );
      setFollowUps(data);
    } catch (error) {
      toast({
        title: "Erro ao carregar follow-ups",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFollowUps();
  }, [filterStatus, filterType, currentPage, selectedClinicId]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateFollowUpStatus(id, newStatus);
      toast({ title: `Status atualizado para ${STATUS_LABELS[newStatus]}` });
      loadFollowUps();
    } catch (error) {
      toast({
        title: "Erro ao atualizar status",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleOpenNotes = (fu: FollowUp) => {
    setSelectedFollowUp(fu);
    setNotesText(fu.notes || "");
    setNotesDialogOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedFollowUp) return;
    try {
      await updateFollowUpNotes(selectedFollowUp.id, notesText);
      toast({ title: "Observacao salva!" });
      setNotesDialogOpen(false);
      loadFollowUps();
    } catch (error) {
      toast({
        title: "Erro ao salvar observacao",
        variant: "destructive",
      });
    }
  };

  const handleClearFilters = () => {
    setFilterStatus("PENDING");
    setFilterType("ALL");
    setFilterName("");
    setCurrentPage(1);
  };

  const filteredFollowUps = filterName
    ? followUps.filter((f) =>
        (f.patient_name || "").toLowerCase().includes(filterName.toLowerCase())
      )
    : followUps;

  return (
    <div className="page-wrapper">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <PhoneCall className="page-icon" />
            <h1 className="page-title">Follow-Ups</h1>
          </div>
          <p className="page-subtitle">
            Pacientes que aceitaram retorno — aguardando contato da recepcionista
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={loadFollowUps}
            disabled={loading}
            className="btn-header"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />{" "}
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <Button
          variant="ghost"
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-3 w-3" />
          {showFilters ? "Esconder Filtros" : "Mostrar Filtros"}
        </Button>

        {showFilters && (
          <div className="filter-box">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <Label className="filter-label">Status</Label>
                <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}>
                  <SelectTrigger className="filter-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="PENDING">Pendente</SelectItem>
                    <SelectItem value="IN_PROGRESS">Em andamento</SelectItem>
                    <SelectItem value="DONE">Concluido</SelectItem>
                    <SelectItem value="CANCELLED">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="filter-label">Tipo</Label>
                <Select value={filterType} onValueChange={(v) => { setFilterType(v); setCurrentPage(1); }}>
                  <SelectTrigger className="filter-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="RECALL_CALLBACK">Recall 6M</SelectItem>
                    <SelectItem value="NOSHOW_CALLBACK">No-Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="filter-label">Nome do paciente</Label>
                <Input
                  className="filter-input"
                  placeholder="Buscar por nome..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="btn-header"
                  onClick={handleClearFilters}
                >
                  Limpar filtros
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="space-y-2">
        <p className="page-counter">
          {filteredFollowUps.length} follow-up(s) encontrado(s)
        </p>

        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="th">Paciente</TableHead>
                  <TableHead className="th">Telefone</TableHead>
                  <TableHead className="th">Tipo</TableHead>
                  <TableHead className="th">Status</TableHead>
                  <TableHead className="th">Criado em</TableHead>
                  <TableHead className="th">Observacao</TableHead>
                  <TableHead className="th text-center">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j} className="td">
                          <div className="h-4 bg-muted animate-pulse rounded" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredFollowUps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="td text-center text-muted-foreground py-8">
                      Nenhum follow-up encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFollowUps.map((fu) => (
                    <TableRow key={fu.id} className="hover:bg-muted/30 transition-colors">
                      <TableCell className="td font-medium">
                        {fu.patient_name || "-"}
                      </TableCell>
                      <TableCell className="td">
                        {formatPhone(fu.patient_phone ?? null)}
                      </TableCell>
                      <TableCell className="td">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary">
                          {TYPE_LABELS[fu.follow_up_type] || fu.follow_up_type}
                        </span>
                      </TableCell>
                      <TableCell className="td">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            STATUS_COLORS[fu.follow_up_status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {STATUS_LABELS[fu.follow_up_status] || fu.follow_up_status}
                        </span>
                      </TableCell>
                      <TableCell className="td">
                        {formatDateBR(fu.created_at)}
                      </TableCell>
                      <TableCell className="td max-w-[200px] truncate">
                        <button
                          onClick={() => handleOpenNotes(fu)}
                          className="text-left hover:underline text-muted-foreground"
                          title={fu.notes || "Clique para adicionar"}
                        >
                          {fu.notes || "—"}
                        </button>
                      </TableCell>
                      <TableCell className="td text-center">
                        <div className="flex items-center justify-center gap-1">
                          {fu.follow_up_status === "PENDING" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 hover:bg-blue-500/10"
                              title="Iniciar contato"
                              onClick={() => handleStatusChange(fu.id, "IN_PROGRESS")}
                            >
                              <Play className="h-3.5 w-3.5 text-blue-600" />
                            </Button>
                          )}
                          {(fu.follow_up_status === "PENDING" ||
                            fu.follow_up_status === "IN_PROGRESS") && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-green-500/10"
                                title="Concluir"
                                onClick={() => handleStatusChange(fu.id, "DONE")}
                              >
                                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-red-500/10"
                                title="Cancelar"
                                onClick={() => handleStatusChange(fu.id, "CANCELLED")}
                              >
                                <XCircle className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
          <span className="page-counter">Pagina {currentPage}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="btn-header"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="btn-header"
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={filteredFollowUps.length < PAGE_SIZE}
            >
              Proxima
            </Button>
          </div>
        </div>
      </div>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Observacoes — {selectedFollowUp?.patient_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Adicione uma observacao sobre o contato..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveNotes}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
